// src/encounter/care-plan-tab/care-plan-marks.tsx
// The plan board's small marks, as the demo draws them: the state glyph (done ✓ / half ◑ / empty ○), the "AI draft"
// chip, the lane icon tile, a status line, and the Review / Sign action with its explicit second step.
import { useState } from 'react';
import { Check, Circle, Contrast, Sparkles } from 'lucide-react';
import { StatusChip } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import type { CarePlanActivityView, CarePlanLane } from '../lib/care-plan-view';
import type { ActivityAction } from './use-activity-action';
import { ACTIVITY_STATUS_LABEL, CARE_PLAN_LANE_ICON, CARE_PLAN_LANE_LOOK, CP_LINK_ACTION } from './care-plan-labels';

/** The demo's Xe: done = all signed, half = some or accepted, empty = none. */
export function CarePlanStateGlyph({ kind }: { kind: 'done' | 'half' | 'empty' }) {
  if (kind === 'done') return <Check size={12} aria-hidden className="shrink-0 text-status-success" />;
  if (kind === 'half') return <Contrast size={12} aria-hidden className="shrink-0 text-accent-primary" />;
  return <Circle size={12} aria-hidden className="shrink-0 text-ink-meta" />;
}

/** The demo's Vp: a soft info chip carrying the intelligence glyph and "AI draft". */
export function AiDraftChip() {
  return (
    <StatusChip semantic="info" tone="soft" size="xs" title="Drafted by Scrips Intelligence · awaiting the named owner">
      <span className="inline-flex items-center gap-1">
        <Sparkles size={12} aria-hidden />
        {ACTIVITY_STATUS_LABEL['ai-draft']}
      </span>
    </StatusChip>
  );
}

/** The demo's state line under a title: the AI draft chip, a "Draft" chip, or the glyph and the status words. */
export function ActivityStateLine({ activity: a, className }: { activity: CarePlanActivityView; className?: string }) {
  if (a.status === 'ai-draft') return <AiDraftChip />;
  if (a.status === 'draft')
    return (
      <StatusChip semantic="neutral" tone="soft" size="xs">
        {ACTIVITY_STATUS_LABEL.draft}
      </StatusChip>
    );
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <CarePlanStateGlyph kind={a.status === 'signed' ? 'done' : 'half'} />
      {a.status === 'signed' && a.signedBy ? `Signed · ${a.signedBy}` : ACTIVITY_STATUS_LABEL[a.status]}
    </span>
  );
}

/** The lane's icon on its hue: 32 on the lane, 24 in the legend, a bar and a Needs-you-now row (the demo's sizes). */
export function LaneIconTile({ lane, size }: { lane: CarePlanLane; size: 'lane' | 'small' }) {
  const Glyph = CARE_PLAN_LANE_ICON[lane];
  return (
    <span
      aria-hidden
      data-lane-icon={lane}
      className={cn(
        'grid shrink-0 place-items-center',
        size === 'lane' ? 'size-8 rounded-md' : 'size-6 rounded-sm',
        CARE_PLAN_LANE_LOOK[lane].tile,
      )}
    >
      <Glyph size={16} aria-hidden />
    </span>
  );
}

/** Review (accept) or Sign with a second, explicit step: an AI draft is never accepted on one click. */
export function ActivityActionButton({
  kind,
  busy,
  onConfirm,
}: {
  kind: ActivityAction;
  /** Any Accept / Sign in flight: every button waits (one request at a time). */
  busy: boolean;
  onConfirm: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  if (!confirming)
    return (
      <button type="button" data-item-action={kind} disabled={busy} className={CP_LINK_ACTION} onClick={() => setConfirming(true)}>
        {kind === 'sign' ? 'Sign' : 'Review'}
      </button>
    );
  return (
    <span className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        disabled={busy}
        className={cn(CP_LINK_ACTION, 'font-semibold')}
        onClick={() => {
          setConfirming(false);
          onConfirm();
        }}
      >
        {kind === 'sign' ? 'Confirm sign' : 'Confirm accept'}
      </button>
      <button type="button" className={CP_LINK_ACTION} onClick={() => setConfirming(false)}>
        Cancel
      </button>
    </span>
  );
}
